import { useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import NavFooter from "./components/NavFooter";
import LanguageContext from "./components/LanguageContext";
import Home from "./pages/Home";
import Services from "./pages/Services";
import About from "./pages/About";
import "./App.css";

function App() {
  const [lang, setLang] = useState("zh");
  const [transitioning, setTransitioning] = useState(false);
  const handleSetLang = (newLang) => {
    if (newLang === lang) return;
    setTransitioning(true);
    setTimeout(() => {
      setLang(newLang);
      setTransitioning(false);
    }, 400);
  };

  const t = translations[lang];
  const location = useLocation();

  return (
    <LanguageContext.Provider value={{ lang, setLang: handleSetLang, t }}>
      <NavFooter>
        {transitioning && <div className="lang-transition-overlay" />}
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/about" element={<About />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AnimatePresence>
      </NavFooter>
    </LanguageContext.Provider>
  );
}
const translations = {
  zh: {
    brandTitle: "心愈养生馆",
    brandSubtitle: "中式经络 · 养身调理",
    navHome: "首页",
    navServices: "服务项目",
    navAbout: "关于我们",
    navContact: "联络我们",
    findUsTitle: "我们门店的位置",
    findUsSubtitle: "您可以在这里找到我们",
    heroTagline: "身心放松 · 回归平衡",
    heroTitle: "在城市中心，给自己一段安静的疗愈时光",
    heroText:
      "结合传统中式推拿与经络养生，为长期久坐、肩颈酸痛、失眠乏力的您，打造一处安心放松的小空间。",
    servicesTitle: "服务项目",
    servicesIntro: "根据个人体质与需要，由理疗师一对一调整按压力度与手法。",
    services: [
      {
        name: "全身中式油推",
        desc: "采用中式推拿结合精油手法，针对肩颈、背部与肌肉群，有效缓解长期久坐或劳累引起的酸痛与僵硬。",
        benefits: [
          "深层放松肌肉，缓解肩颈与背部酸痛",
          "改善血液循环，恢复身体柔韧性",
          "缓解长期久坐引起的疲劳与酸痛"
        ],
        duration: "60 / 90 / 120 分钟",
        price: "60 分钟: S$50 · 90 分钟: S$75 · 120 分钟: S$98",
        minPrice: '从 60 分钟: S$50 起',
        image: '/images/OilMassage.jpg'
      },
      {
        name: "热油经络放松",
        desc: "以温热精油配合经络疏导手法，打通气血通道，驱散体内寒湿，舒缓经络紧张，帮助身体深度放松。",
        benefits: [
          "促进气血循环，疏通经络",
          "改善睡眠质量",
          "缓解身体僵硬与寒湿感"
        ],
        duration: "60 / 90 / 120 分钟",
        price: "60 分钟: S$60 · 90 分钟: S$85 · 120 分钟: S$115",
        minPrice: '从 60 分钟: S$60 起',
        image: '/images/HotOil.jpg'
      },
      {
        name: "胸部 / 卵巢保养",
        desc: "通过温和手法刺激相关经络与反射区，帮助调理女性内分泌系统，促进身体平衡与舒适。",
        benefits: [
          "调节女性内分泌与体质",
          "缓解疲劳紧张与不安情绪",
          "提升身体舒适感与整体健康"
        ],
        duration: "60 / 90 分钟",
        price: "60 分钟: S$60 · 90 分钟: S$90",
        minPrice: '从 60 分钟: S$60 起',
        image: '/images/Breast.avif'
      },
      {
        name: "脚部 + 肩颈背",
        desc: "结合足部按摩与肩颈背推拿，同时放松下肢与上半身，有效缓解整体疲劳。",
        benefits: [
          "同时放松上下半身，提升整体舒适感",
          "改善全身血液循环，缓解疲劳与紧张",
          "帮助身体快速恢复活力"
        ],
        duration: "60 / 70 / 80 分钟",
        price: "60 分钟: S$50 · 70 分钟: S$58 · 80 分钟: S$68",
        minPrice: '从 60 分钟: S$50 起',
        image: '/images/FootSNB.jpg'
      },
      {
        name: "刮痧 / 拔罐 / 采耳",
        desc: "传统中医调理方式，帮助疏通经络、排除体内湿气与疲劳。",
        benefits: [
          "疏通经络，促进气血流通",
          "缓解肌肉紧张与酸痛",
          "排除体内湿气与毒素，促进身体排毒"
        ],
        duration: "30 分钟",
        price: "30 分钟: $20",
        minPrice: '30 分钟: $20',
        image: '/images/Cupping.avif'
      },
      {
        name: "脚底抑菌理疗",
        desc: "针对足部健康进行抑菌与护理，帮助保持足部清爽与舒适。",
        benefits: [
          "改善足部卫生与舒适度",
          "减少异味与不适",
          "维护足部健康"
        ],
        duration: "30 / 60 / 90 分钟",
        price: "30 分钟: S$18 · 60 分钟: S$35 · 90 分钟: S$48",
        minPrice: '从 30 分钟: S$18 起',
        image: '/images/FootTreatment.avif'
      },
      {
        name: "古法瑶熨理疗",
        desc: "源自瑶族传统养生疗法，通过草药热敷理疗，将天然草药包加热后敷于身体特定部位，沿经络穴位按压滚动，以热力与药性驱除湿气，疏通气血，缓解身体疲劳与不适。",
        benefits: [
          "驱散体内湿气与寒气，温补阳气",
          "促进气血循环与新陈代谢",
          "缓解肌肉酸痛，关节疼痛"
        ],
        duration: "30 / 60 / 90 分钟",
        price: "30 分钟: S$35 · 60 分钟: S$65 · 90 分钟: S$95",
        minPrice: '从 30 分钟: S$35 起',
        image: '/images/YaoYun.avif'
      },
      {
        name: "头部 + 脸部拔筋",
        desc: "针对头部与脸部经络进行舒筋理疗，通过刺激经络穴位与筋膜，缓解头痛，排除毒素与代谢废物，改善头皮与面部血液循环",
        benefits: [
          "改善头部血液循环，面部肌肉紧张与疲劳",
          "缓解失眠，头痛与压力",
          "消除面部浮肿，改善面色暗沉与色斑"
        ],
        duration: " 60 分钟",
        price: "60 分钟: S$60",
        minPrice: '60 分钟: S$60',
        image: '/images/HeadFacial.avif'
      },
      {
        name: "传统泥灸疗法",
        desc: "结合艾草，泥矿物质与中草药精华融合制造的草本热敷泥，敷于特定穴位与经络部位，通过热力与药性渗透，温通经络，驱散寒湿，达到深层调理与修复。",
        benefits: [
          "舒经活络，大补元气，增强免疫力",
          "深度排汗排毒，促进新陈代谢",
          "美容养颜，润肤止痒",
        ],
        duration: "60 / 90 分钟",
        price: "60 分钟: S$78 · 90 分钟: S$108",
        minPrice: '从 60 分钟: S$78 起',
        image: '/images/MudMoxi.avif'
      }
    ],
    aboutTitle: "关于心愈养生馆",
    aboutText1:
      "心愈养生馆专注于传统中式经络调理，为每一位顾客提供专业、实惠、舒适的养生体验。",
    aboutHighlights: [
      { text: "位于牛车水地铁站旁的人民珍珠坊，交通便利，易于到达" },
      { text: "经验丰富的理疗师，根据个人体质一对一调理" },
      { text: "采用传统中式手法，结合优质精油与理疗技术" },
      { text: "价格实惠，多种疗程时长灵活选择" },
      { text: "每日营业 9:30AM – 9:30PM，无需预约" },
      { text: "提供多种中式理疗项目，满足不同身体需求" }
    ],
    contactTitle: "联络我们",
    contactText: "如需咨询或预约，请通过电话或 WhatsApp 与我们联系。",
    phoneLabel: "联系电话",
    addressLabel: "地址",
    hoursLabel: "营业时间",
    paymentLabel: "付款方式",
    paymentLabels: "现金 · PayNow",
    modalPriceTitle: "价格",
    modalDescTitle: "介绍",
    modalBenefitsTitle: "功效",
    callNow: "立即拨打",
  },
  en: {
    brandTitle: "Xin Yu Yang Shen Guan",
    brandSubtitle: "Chinese Meridian · Body Care",
    navHome: "Home",
    navServices: "Services",
    navAbout: "About Us",
    navContact: "Contact Us",
    findUsTitle: "Find Us",
    findUsSubtitle: "Locate us here",
    heroTagline: "Relax body & mind",
    heroTitle: "A calm healing space in the heart of the city",
    heroText:
      "We combine traditional Chinese massage and meridian techniques to ease tension, improve sleep and gently restore balance to your body.",
    servicesTitle: "Our Services",
    servicesIntro:
      "Treatments are customised to your body condition with gentle, attentive one-to-one care.",
    services: [
      {
        name: "Whole Body Chinese-style Oil Massage",
        desc: "A traditional Chinese oil massage that targets neck, shoulders and back to release deep muscle tension, restore circulation and restore phsyical comfort.",
        benefits: [
          "Relieves deep muscle tension in neck, shoulders and back",
          "Improves blood circulation and flexibility",
          "Reduces stress and physical fatigue",
        ],
        duration: "60 / 90 / 120 Mins",
        price: "60 Mins: S$50 · 90 Mins: S$75 · 120 Mins: S$98",
        minPrice: 'From 60 Mins: S$50',
        image: '/images/OilMassage.jpg'
      },
      {
        name: "Hot Oil Meridian Relexation",
        desc: "Warm therapeutic oil combined with meridian techniques to relax the body, improve circulation, ease stiffness and promote deep relaxation.",
        benefits: [
          "Enhances circulation and meridian flow",
          "Supports better sleep quality",
          "Relieves body stiffness and cold tension"
        ],
        duration: "$60 / 90 / 120 mins",
        price: "60 Mins: S$60 · 90 Mins: S$85 · 120 Mins: S$115",
        minPrice: 'From 60 Mins: S$60',
        image: '/images/HotOil.jpg'
      },
      {
        name: "Breast and Ovary Care",
        desc: "A gentle wellness therapy designed to support circulation and relaxation through targeted meridian techniques.",
        benefits: [
          "Supports female hormone balance and internal regulation",
          "Relieves tension, fatigue and emotional stress",
          "Helps relieve overall comfort and well-being"
        ],
        duration: "$60 / 90 mins",
        price: "60 Mins: S$60 · 90 Mins: S$90",
        minPrice: 'From 60 Mins: S$60',
        image: '/images/Breast.avif'
      },
      {
        name: "Foot + Shoulder, Neck and Back Massage",
        desc: "A combination therapy that targets both the feet and upper body to relieve full-body fatigue.",
        benefits: [
          "Relieves tension from head to toe",
          "Improves overall circulation， reduce physical fatigue",
          "Enhances full-body comfort and relaxation"
        ],
        duration: "$60 / 70 / 80 mins",
        price: "60 Mins: S$50 · 70 Mins: S$58 · 80 Mins: S$68",
        minPrice: 'From 60 Mins: S$50',
        image: '/images/FootSNB.jpg'
      },
      {
        name: "Scraping / Cupping / Ear Cleaning Therapy",
        desc: "Traditional Chinese therapies designed to release tension and improve circulation.",
        benefits: [
          "Clears blocked meridians and improves circulation",
          "Relieves muscle tightness and soreness",
          "Supports body detox by drawing out dampness and toxins"
        ],
        duration: "30 Mins",
        price: "30 Mins: S$20",
        image: '../images/Cupping.avif'
      },
      {
        name: "Foot Antibacterial Therapy",
        desc: "A targeted foot care therapy designed to support hygiene and foot comfort.",
        benefits: [
          "Improves foot cleanliness",
          "Reduces discomfort and odour",
          "Supports overall foot health"
        ],
        duration: "30 / 60 / 90 Mins",
        price: "30 Mins: S$18 · 60 Mins: S$35 · 90 Mins: S$48",
        minPrice: 'From 30 Mins: S$18',
        image: '/images/FootTreatment.avif'
      },
      {
        name: "Traditional Yao Herbal Compress Therapy",
        desc: "A traditional Yao herbal heat therapy where natural herbal packs are heated and applied to specific areas of the body, pressed and rolled along meridian points to draw out dampness, improve circulation and relieve fatigue and discomfort.",
        benefits: [
          "Draws out dampness and cold from the body, warming and restoring yang energy",
          "Improves qi and blood circulation and boosts metabolism",
          "Relieves muscle soreness and joint pain"],
        duration: "30 / 60 / 90 Mins",
        price: "30 Mins: S$35 · 60 Mins: S$65 · 90 Mins: S$95",
        minPrice: 'From 30 Mins: S$35',
        image: '/images/YaoYun.avif'
      },
      {
        name: "Head & Facial Meridian Therapy",
        desc: "A meridian stretching therapy targeting the head and face, stimulating meridian points and fascia to relieve headaches, eliminate toxins and metabolic waste, and improve scalp and facial blood circulation.",
        benefits: [
          "Improves head blood circulation and relieves facial muscle tension and fatigue",
          "Eases insomnia, headaches and stress",
          "Reduces facial puffiness and improves dull complexion and uneven skin tone"],
        duration: "60 Mins",
        price: "60 Mins: S$60",
        minPrice: '60 Mins: S$60',
        image: '/images/HeadFacial.avif'
      },
      {
        name: "Traditional Chinese Mud Therapy",
        desc: "A herbal heat therapy mud combining mugwort, mineral clay and Chinese herbal extracts, applied to specific acupoints and meridian areas. The heat and herbal properties penetrate to warm and unblock meridians, dispel cold and dampness, achieving deep therapeutic condition and repairs the body.",
        benefits: [
          "Relaxe meridian, greatly replenishes vital energy and strengthens immunity",
          "Deep detoxification, promotes metabolism",
          "Beautifies and nourishes the skin, moisturies and relieves itchiness",
        ],
        duration: "60 / 90 Mins",
        price: "60 Mins: S$78 · 90 Mins: S$108",
        minPrice: 'From 60 Mins: S$78',
        image: '/images/MudMoxi.avif'
      }
    ],
    aboutTitle: "About Xin Yu Wellness",
    aboutText1:
      "We believe true relaxation is not only in the body but also in the heart.",
    aboutHighlights: [
      { text: "Conveniently located at Chinatown MRT, People's Park Complex, easy to access" },
      { text: "Experienced therapists providing personalized care based on individual constitution" },
      { text: "Utilizing traditional Chinese techniques combined with high-quality essential oils and massage methods" },
      { text: "Affordable prices with flexible session durations to suit your needs" },
      { text: "Open daily from 9:30 AM to 9:30 PM, no appointment needed" },
      { text: "A variety of Chinese wellness treatments to suit different body needs" }],

    contactTitle: "Contact Us",
    contactText:
      "For enquiries or appointments, please contact us via phone or WhatsApp.",
    phoneLabel: "Phone",
    addressLabel: "Address",
    hoursLabel: "Opening Hours",
    paymentLabel: "Payment Methods",
    paymentLabels: "Cash · PayNow",
    modalPriceTitle: "Price",
    modalDescTitle: "Description",
    modalBenefitsTitle: "Benefits",
    callNow: "Call Now",
  }
};

function NotFound() {
  return (
    <motion.div
      className="page"
      style={{ textAlign: "center", paddingTop: "4rem" }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 style={{ fontSize: "3rem", margin: "0" }}>404</h2>
      <p style={{ opacity: 0.85, margin: "1rem 0 2rem" }}>
        页面不存在 · Page not found
      </p>
      <a href="/" className="primary-btn">
        返回首页 · Back to Home
      </a>
    </motion.div>
  );
}

export default App;
